/**
 * EntityDetailsCard — renders full detail for one corporate entity:
 * addresses, directors/officers, shareholders, change history.
 */

import { useState } from "react";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import { Building2, ChevronDown } from "lucide-react";
import { SourcePills } from "./SourcePill";
import { CompactEntityCard } from "./CompactEntityCard";

// ---------------------------------------------------------------------------
// Types (mirrors backend HierarchicalCorporateSummary sub-types)
// ---------------------------------------------------------------------------

interface SourceCitation {
  doc: string;
  docId?: string;
  page?: number;
}
interface CorporateAddress {
  type: string;
  fullAddress: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  sources: SourceCitation[];
}
interface DirectorOrOfficer {
  name: string;
  aliases?: string[];
  dateOfBirth?: string;
  addresses: CorporateAddress[];
  appointments: Array<{
    role: string;
    appointedDate?: string;
    ceasedDate?: string;
    isCurrent: boolean;
    sources: SourceCitation[];
  }>;
  sources: SourceCitation[];
}
interface Shareholder {
  name: string;
  aliases?: string[];
  holderType: string;
  address?: string;
  holdings: Array<{
    shareClass: string;
    numberOfShares: number;
    paidAmount?: number;
    beneficiallyOwned: boolean;
    sources: SourceCitation[];
  }>;
  sources: SourceCitation[];
}
interface ChangeHistoryItem {
  changeType: string;
  effectiveDate?: string;
  description: string;
  sources: SourceCitation[];
}

export interface CorporateEntityDetail {
  entityName: string;
  acn?: string;
  abn?: string;
  entityType?: string;
  registrationStatus?: string;
  dateOfIncorporation?: string;
  jurisdiction?: string;
  addresses: CorporateAddress[];
  directorsAndOfficers: DirectorOrOfficer[];
  shareholders: Shareholder[];
  changeHistory: ChangeHistoryItem[];
  sources: SourceCitation[];
}

interface EntityDetailsCardProps {
  entity: CorporateEntityDetail;
  onViewDocument?: (docId: string) => void;
  holdingCompany?: CorporateEntityDetail | null;
  subsidiaries?: CorporateEntityDetail[];
  sisterCompanies?: CorporateEntityDetail[];
  onRelatedEntityClick?: (entity: CorporateEntityDetail, label: string) => void;
  /**
   * Personal-insolvency jobs drop the "Corporate" qualifier from the card
   * header ("Corporate Summary" → "Summary"), since the estate is an
   * individual, not a company. Defaults to the corporate wording.
   */
  isBankruptcy?: boolean;
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const PALETTES = [
  {
    bg: "bg-blue-100 dark:bg-blue-900/40",
    text: "text-blue-700 dark:text-blue-300",
  },
  {
    bg: "bg-violet-100 dark:bg-violet-900/40",
    text: "text-violet-700 dark:text-violet-300",
  },
  {
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    text: "text-amber-700 dark:text-amber-300",
  },
  {
    bg: "bg-pink-100 dark:bg-pink-900/40",
    text: "text-pink-700 dark:text-pink-300",
  },
  {
    bg: "bg-teal-100 dark:bg-teal-900/40",
    text: "text-teal-700 dark:text-teal-300",
  },
];

function Avatar({ name, size = "sm" }: { name: string; size?: "sm" | "md" }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const hash = Array.from(name).reduce(
    (acc, ch) => ch.charCodeAt(0) + ((acc << 5) - acc),
    0,
  );
  const p = PALETTES[Math.abs(hash) % PALETTES.length];
  const sizeClass = size === "md" ? "h-9 w-9 text-xs" : "h-7 w-7 text-[10px]";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        sizeClass,
        p.bg,
        p.text,
      )}
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OfficerRow
// ---------------------------------------------------------------------------

function OfficerRow({
  officer: d,
  showBorderTop,
  onViewDocument,
}: {
  officer: DirectorOrOfficer;
  showBorderTop: boolean;
  onViewDocument?: (docId: string) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 py-3",
        showBorderTop && "border-t border-border/50",
      )}
    >
      <Avatar name={d.name} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{d.name}</span>
          {d.aliases && d.aliases.length > 0 && (
            <span className="text-xs italic text-muted-foreground">
              aka {d.aliases.join(", ")}
            </span>
          )}
        </div>
        {d.dateOfBirth && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            DOB: {formatDate(d.dateOfBirth)}
            {d.addresses.length > 0 && ` · ${d.addresses[0].fullAddress}`}
          </p>
        )}
        {d.appointments.map((a, j) => (
          <div key={j} className="mt-1 flex items-center gap-1.5">
            <Badge
              color={ROLE_COLORS[a.role] ?? "gray"}
              variant="soft"
              size="sm"
              className="capitalize"
            >
              {a.role.replace(/_/g, " ")}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatDate(a.appointedDate) ?? "—"}
              {a.ceasedDate ? ` → ${formatDate(a.ceasedDate)}` : ""}
            </span>
          </div>
        ))}
        <SourcePills sources={d.sources} onViewDocument={onViewDocument} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d?: string): string | null {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

const ROLE_COLORS: Record<
  string,
  "indigo" | "blue" | "green" | "purple" | "gray"
> = {
  director: "indigo",
  secretary: "green",
  public_officer: "blue",
  alternate_director: "purple",
};

const ADDRESS_TYPE_LABELS: Record<string, string> = {
  registered: "Registered Office",
  principal: "Principal Place",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EntityDetailsCard({
  entity,
  onViewDocument,
  holdingCompany,
  subsidiaries = [],
  sisterCompanies = [],
  onRelatedEntityClick,
  isBankruptcy = false,
}: EntityDetailsCardProps) {
  const [shareholdersOpen, setShareholdersOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [holdingOpen, setHoldingOpen] = useState(true);
  const [subsOpen, setSubsOpen] = useState(true);
  const [sistersOpen, setSistersOpen] = useState(true);

  const hasSubs = subsidiaries.length > 0;
  const hasSisters = sisterCompanies.length > 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
      {/* Summary header — drops "Corporate" for individual bankruptcies */}
      <div className="px-5 pb-4 pt-5">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {isBankruptcy ? "Summary" : "Corporate Summary"}
          </span>
          {entity.registrationStatus && (
            <Badge color="red" variant="soft" size="sm">
              {entity.registrationStatus}
            </Badge>
          )}
        </div>
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-2.5 dark:border-blue-800 dark:bg-blue-950/30">
            <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              {entity.entityName}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {entity.acn ? `ACN ${entity.acn}` : ""}
              {entity.acn && entity.abn ? " · " : ""}
              {entity.abn ? `ABN ${entity.abn}` : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Entity Details */}
      {entity.addresses.length > 0 && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 pb-4 pt-4">
            <h4 className="mb-3 font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
              Entity Details
            </h4>
            <div className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-3">
              {entity.addresses.map((addr, i) => (
                <div key={i} className="contents">
                  <span className="pt-0.5 text-xs text-muted-foreground">
                    {ADDRESS_TYPE_LABELS[addr.type] ?? addr.type}
                  </span>
                  <div>
                    <span className="text-sm text-foreground">
                      {addr.fullAddress}
                    </span>
                    <SourcePills
                      sources={addr.sources}
                      onViewDocument={onViewDocument}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Directors & Officers */}
      {entity.directorsAndOfficers.length > 0 && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <h4 className="mb-3 font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
              Directors & Officers
            </h4>
            {(() => {
              const isOfficerCurrent = (d: DirectorOrOfficer) => {
                const latestAppt = d.appointments[d.appointments.length - 1];
                return latestAppt?.isCurrent ?? true;
              };
              const currentOfficers =
                entity.directorsAndOfficers.filter(isOfficerCurrent);
              const formerOfficers = entity.directorsAndOfficers.filter(
                (d) => !isOfficerCurrent(d),
              );

              return (
                <>
                  {currentOfficers.length > 0 && (
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                          Current
                        </span>
                      </div>
                      <div className="space-y-0">
                        {currentOfficers.map((d, i) => (
                          <OfficerRow
                            key={`current-${i}`}
                            officer={d}
                            showBorderTop={i > 0}
                            onViewDocument={onViewDocument}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {formerOfficers.length > 0 && (
                    <div className={cn(currentOfficers.length > 0 && "mt-4")}>
                      <div className="mb-1 flex items-center gap-2">
                        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                          Former
                        </span>
                      </div>
                      <div className="space-y-0">
                        {formerOfficers.map((d, i) => (
                          <OfficerRow
                            key={`former-${i}`}
                            officer={d}
                            showBorderTop={i > 0}
                            onViewDocument={onViewDocument}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Shareholders */}
      {entity.shareholders.length > 0 && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <Collapsible
              open={shareholdersOpen}
              onOpenChange={setShareholdersOpen}
            >
              <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left">
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    shareholdersOpen && "rotate-180",
                  )}
                />
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Shareholders
                </h4>
                <span className="text-[10px] text-muted-foreground">
                  ({entity.shareholders.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 space-y-0">
                  {entity.shareholders.map((sh, i) => (
                    <div
                      key={i}
                      className={cn(
                        "py-3",
                        i > 0 && "border-t border-border/50",
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground">
                              {sh.name}
                            </span>
                            {sh.aliases && sh.aliases.length > 0 && (
                              <span className="text-xs italic text-muted-foreground">
                                aka {sh.aliases.join(", ")}
                              </span>
                            )}
                          </div>
                          {sh.address && (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {sh.address}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {sh.holdings.map((h, j) => (
                            <div key={j} className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold tabular-nums">
                                {h.numberOfShares.toLocaleString()}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {h.shareClass.toLowerCase()}
                              </span>
                              <Badge
                                color={h.beneficiallyOwned ? "green" : "amber"}
                                variant="soft"
                                size="sm"
                              >
                                {h.beneficiallyOwned
                                  ? "Beneficial"
                                  : "Not beneficial"}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                      <SourcePills
                        sources={sh.sources}
                        onViewDocument={onViewDocument}
                      />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}

      {/* Holding Company */}
      {holdingCompany && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <Collapsible open={holdingOpen} onOpenChange={setHoldingOpen}>
              <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left">
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    holdingOpen && "rotate-180",
                  )}
                />
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Holding Company
                </h4>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3">
                  <CompactEntityCard
                    entity={holdingCompany}
                    onClick={() =>
                      onRelatedEntityClick?.(holdingCompany, "holding company")
                    }
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}

      {/* Subsidiaries */}
      {hasSubs && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <Collapsible open={subsOpen} onOpenChange={setSubsOpen}>
              <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left">
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    subsOpen && "rotate-180",
                  )}
                />
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Subsidiaries
                </h4>
                <span className="text-[10px] text-muted-foreground">
                  ({subsidiaries.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {subsidiaries.map((sub, i) => (
                    <CompactEntityCard
                      key={i}
                      entity={sub}
                      onClick={() =>
                        onRelatedEntityClick?.(sub, "subsidiaries")
                      }
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}

      {/* Sister Companies */}
      {hasSisters && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <Collapsible open={sistersOpen} onOpenChange={setSistersOpen}>
              <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left">
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    sistersOpen && "rotate-180",
                  )}
                />
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Sister Companies
                </h4>
                <span className="text-[10px] text-muted-foreground">
                  ({sisterCompanies.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {sisterCompanies.map((sis, i) => (
                    <CompactEntityCard
                      key={i}
                      entity={sis}
                      onClick={() =>
                        onRelatedEntityClick?.(sis, "sister companies")
                      }
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}

      {/* Change History */}
      {entity.changeHistory.length > 0 && (
        <>
          <div className="border-t border-border" />
          <div className="px-5 py-4">
            <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
              <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    historyOpen && "rotate-180",
                  )}
                />
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Changes & Lodgements
                </h4>
                <span className="text-[10px] text-muted-foreground">
                  ({entity.changeHistory.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 space-y-0">
                  {entity.changeHistory.map((ch, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex items-start gap-3 py-2.5",
                        i > 0 && "border-t border-border/50",
                      )}
                    >
                      <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/30" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            size="sm"
                            className="shrink-0 capitalize"
                          >
                            {ch.changeType.replace(/_/g, " ")}
                          </Badge>
                          {ch.effectiveDate && (
                            <span className="text-[10px] tabular-nums text-muted-foreground">
                              {formatDate(ch.effectiveDate)}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                          {ch.description}
                        </p>
                        <SourcePills
                          sources={ch.sources}
                          onViewDocument={onViewDocument}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}
    </div>
  );
}
